import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreditCard, Plus, Pencil, Trash2, History } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { toast } from "sonner";

const empty = { number: "", plate: "", provider: "", notes: "" };
const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" }) : "-";
const STATUS_LABEL = { in_attesa: "In attesa", approvata: "Approvata", rifiutata: "Rifiutata" };

export default function FuelCards() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [providers, setProviders] = useState([]);

  const load = () => api.get("/fuel-cards").then((r) => setItems(r.data));
  const loadProviders = () => api.get("/card-providers").then((r) => setProviders(r.data));
  useEffect(() => { load(); loadProviders(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, provider: providers[0]?.name || "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ number: c.number, plate: c.plate || "", provider: c.provider || "Altro", notes: c.notes || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/fuel-cards/${editing.id}`, form);
      else await api.post("/fuel-cards", form);
      toast.success(editing ? "Carta aggiornata" : "Carta creata");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/fuel-cards/${id}`);
      toast.success("Carta eliminata");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openHistory = async (c) => {
    setHistory({ card: c, data: null });
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/fuel-cards/${c.id}/requests`);
      setHistory({ card: c, data });
    } catch (err) {
      toast.error("Errore nel caricamento dello storico");
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Carte carburante</h1>
          <p className="mt-1 text-sm text-slate-500">Crea e gestisci le carte carburante da assegnare ai dipendenti.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="add-card-btn">
              <Plus className="mr-2 h-4 w-4" /> Nuova carta
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="card-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Modifica carta" : "Nuova carta carburante"}</DialogTitle>
              <DialogDescription>Inserisci il gestore e il numero identificativo della carta.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Gestore</Label>
                <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="card-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {providers.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="c-number">Numero / Identificativo</Label>
                <Input id="c-number" data-testid="card-number-input" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1.5" placeholder="es. 7083-1234-5678" />
              </div>
              <div>
                <Label htmlFor="c-plate">Targa associata</Label>
                <Input id="c-plate" data-testid="card-plate-input" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} className="mt-1.5" placeholder="es. AB123CD" />
              </div>
              <div>
                <Label htmlFor="c-notes">Note</Label>
                <Input id="c-notes" data-testid="card-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Facoltative" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} disabled={!form.number} data-testid="save-card-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <DataTable
            data={items}
            testid="cards-table"
            rowTestId={(c) => `card-row-${c.id}`}
            searchKeys={["provider", "number", "plate", "notes"]}
            searchPlaceholder="Cerca carta..."
            initialSort={{ key: "provider", dir: "asc" }}
            emptyIcon={CreditCard}
            emptyText="Nessuna carta"
            columns={[
              { key: "provider", header: "Gestore", sortable: true, className: "font-medium text-slate-900", cell: (c) => c.provider || "-" },
              { key: "number", header: "Numero", sortable: true, className: "text-slate-600", cell: (c) => c.number },
              { key: "plate", header: "Targa", sortable: true, className: "text-slate-600", cell: (c) => c.plate ? <span className="font-medium text-slate-700">{c.plate}</span> : <span className="text-slate-300">—</span> },
              { key: "notes", header: "Note", sortable: true, className: "text-slate-500", cell: (c) => c.notes || "-" },
              { key: "actions", header: "Azioni", align: "right", stacked: true, cell: (c) => (
                  <div className="flex flex-wrap justify-end gap-2 md:flex-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openHistory(c)} data-testid={`history-card-${c.id}`} className="rounded-md">
                      <History className="mr-2 h-4 w-4" /> Storico
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-card-${c.id}`} className="rounded-md">
                      <Pencil className="mr-2 h-4 w-4" /> Modifica
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(c.id)} data-testid={`delete-card-${c.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50">
                      <Trash2 className="mr-2 h-4 w-4" /> Elimina
                    </Button>
                  </div>
                ) },
            ]}
          />
        </div>
      </Card>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-2xl" data-testid="card-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Storico carta {history?.card?.label}
            </DialogTitle>
            <DialogDescription>Totale speso e richieste collegate a questa carta.</DialogDescription>
          </DialogHeader>
          {historyLoading || !history?.data ? (
            <p className="py-8 text-center text-sm text-slate-400">Caricamento...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Speso (approvato)</p>
                  <p className="font-heading text-xl font-extrabold text-[#002FA7]" data-testid="history-total-approved">
                    {fmtEur(history.data.total_approved)}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">In attesa</p>
                  <p className="font-heading text-xl font-extrabold text-amber-600">
                    {fmtEur(history.data.total_pending)}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Richieste</p>
                  <p className="font-heading text-xl font-extrabold text-slate-900">{history.data.count}</p>
                </div>
              </div>

              {history.data.requests.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Nessuna richiesta collegata a questa carta.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                  <Table data-testid="card-history-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Dipendente</TableHead>
                        <TableHead>Mezzo</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead>Stato</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.data.requests.map((r) => (
                        <TableRow key={r.id} className="hover:bg-slate-50">
                          <TableCell className="text-slate-500">{fmtDate(r.created_at)}</TableCell>
                          <TableCell className="text-slate-700">{r.employee_name}</TableCell>
                          <TableCell className="text-slate-600">{r.vehicle_name}</TableCell>
                          <TableCell className="text-right font-heading font-bold text-slate-900">{fmtEur(r.amount)}</TableCell>
                          <TableCell className="text-slate-600">{STATUS_LABEL[r.status] || r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
